import { useEffect, useState } from 'react'
import { endImpersonation } from '@lb/domain'
import { pathInLocale, useTranslation } from '@lb/i18n'

import { auth, http, queryClient } from '../../lib/client'
import { useSessionContext } from '../providers/session'

/**
 * « You are viewing Suzie's account » — across the top of every page while an
 * administrator is logged in as a member (16 Oct 2026).
 *
 * It is the one thing on screen that says the person looking is not the member,
 * so it is loud on purpose (the brand's alert colour), sticky, and never
 * dismissable. « Return to admin » ends the session **on the server** first —
 * the session row is marked ended and its tokens revoked — then forgets the
 * tab's pair and lands on a page that says it is over. A failed request still
 * forgets the pair locally: the session runs out on its own within thirty
 * minutes either way, and leaving the viewer signed in as the member because a
 * request failed is the worse outcome.
 */
export function ImpersonationBar() {
  const { t, i18n } = useTranslation()
  const session = useSessionContext().viewer?.session
  const [ending, setEnding] = useState(false)

  const view = session?.impersonation

  // The top bar is sticky at the top too; while this bar is shown it sits under
  // it by exactly this bar's height, rather than sliding beneath it.
  useEffect(() => {
    if (!view) return
    document.documentElement.style.setProperty('--impersonation-h', '44px')
    return () => {
      document.documentElement.style.removeProperty('--impersonation-h')
    }
  }, [view])

  if (!session || !view) return null

  const time = new Intl.DateTimeFormat(i18n.language.startsWith('en') ? 'en-CA' : 'fr-CA', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(view.expiresAt))

  const leave = async () => {
    setEnding(true)
    try {
      await endImpersonation(http)
    } catch {
      // Forget the pair anyway — see above.
    }
    auth.clear()
    queryClient.clear()
    const ended = pathInLocale('/account/impersonate', i18n.language.startsWith('en') ? 'en' : 'fr')
    window.location.replace(`${ended}#ended`)
  }

  return (
    <div
      role="status"
      className="sticky top-0 z-30 flex h-[44px] items-center justify-between gap-3 bg-alert px-4 text-[13.5px] text-on-alert"
    >
      <p className="min-w-0 truncate">
        <strong className="font-semibold">{t('impersonation.barLabel', { name: session.fullName })}</strong>
        <span className="ml-2 opacity-90">{t('impersonation.barDetail', { admin: view.adminName, time })}</span>
      </p>
      <button
        type="button"
        onClick={() => void leave()}
        disabled={ending}
        className="flex-none rounded-full bg-on-alert px-3.5 py-1.5 text-[13px] font-semibold text-alert disabled:opacity-60"
      >
        {ending ? t('impersonation.ending') : t('impersonation.return')}
      </button>
    </div>
  )
}
